/* product-card.js — THE catalogue card, one renderer for every live grid.
 *
 * data-price is CENTS (what snippets/product-card.liquid emits and what
 * project-list.js / the cart hint expect). The index carries DOLLARS, so the
 * PLP template had been handing the project list a dollar figure it then read
 * as cents — a $589 vanity saved from a Typesense grid showed as $5.89 in the
 * Lists drawer (found verifying theme PR #343 on 2026-09-17). Converted here.
 *
 * Luke, 2026-09-17: "The product cards [in global search] should have exactly
 * the same functionality as they do in the product catalog grid with mouseover
 * for quick view, add to cart, add to list, tags, etc. Those cards should be
 * identical in every way ... it should all work off of the same components."
 *
 * Until this file the card lived as the InstantSearch `hitTemplate` inside
 * assets/typesense-plp.js (PLP-only), the search dialog drew its own
 * `.sd-tile`, and src/islands/plp/Tile.jsx was a third copy behind the
 * ?plp=react flag. Now typesense-plp.js's hitTemplate and search-dialog.js both
 * call STCard.render(), so a change here is a change everywhere.
 *
 * Contract (unchanged from the hitTemplate, class for class, data-* for data-*):
 *   .card / .card__media.media (data-card-images for the hover carousel,
 *   ui.js) / .card__arrow / .card__flags (window.STBadges, the ONLY badge
 *   authority — scripts/badge-parity.mjs scans this file) / .card__actions
 *   [data-wishlist] / .card__cta [data-quick-view] [data-card-atc]
 *
 * Shape note: the markup is the PLP hitTemplate as the BROWSER holds it, not
 * as the source reads. InstantSearch ran that template through Preact, which
 * serialises bare attributes as ="true" and writes style through cssText
 * (attribute last, `aspect-ratio: 4 / 5;`, `--sw: #hex;`). Measured on the
 * production theme 2026-09-17 (24/24 cards). Since this string now goes in
 * as innerHTML, it is written in that final shape so a card's outerHTML is
 * byte-identical on the collection grid before and after this file.
 * (2026-09-23: the media box's aspect-ratio is now var(--card-ratio, 1/1),
 * the card image theme setting, so that one declaration differs from the
 * 09-17 capture by design.)
 *   [data-cfg-prefetch] / .card__vendor / .card__body .card__type .card__title
 *   .delivery-eta / .rating / .card__price / .card__swatches [data-fin-img].
 *   theme.css holds .card__media at opacity 0 behind a loadbar until
 *   assets/media-ready.js adds .is-ready — renderer-agnostic, so dialog cards
 *   get the same reveal with no wiring here.
 *
 * Classic script, loaded deferred from layout/theme.liquid after
 * product-badges.js and before ui.js. Returns an HTML STRING (there is no
 * `html` tag here), so every interpolated value is escaped by esc().
 */
(function () {
  'use strict';
  if (window.STCard) return;

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var money = function (n) {
    var cfg = window.TYPESENSE || {};
    try { return Number(n).toLocaleString('en-AU', { style: 'currency', currency: cfg.currency || 'AUD' }); }
    catch (e) { return '$' + n; }
  };

  // The index stores cdn.shopify.com/s/files/… originals but Liquid emits
  // {shop}/cdn/shop/… — DIFFERENT cache keys for the same file. Normalise to
  // the Liquid form so tiles, PDP and quick view all share one cache.
  var cdn = function (u, w) {
    return u
      ? u.replace(/^(https?:)?\/\/cdn\.shopify\.com\/s\/files\/1\/\d+\/\d+\/\d+\//, '//' + location.host + '/cdn/shop/')
        + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w
      : u;
  };
  /* MEASURED tile geometry, byte-identical to snippets/grid-sizes.liquid
   * (layout: 'rail'), typesense-plp.js and src/islands/plp/Tile.jsx. */
  var GRID_SIZES = '(min-width:3841px) 740px, (min-width:2600px) 20vw, (min-width:2048px) 25vw, (min-width:1440px) 36vw, (min-width:1001px) 44vw, (min-width:981px) 38vw, (min-width:681px) 44vw, 66vw';
  var GRID_WIDTHS = [200, 250, 310, 380, 460, 550, 660, 790, 940, 1100];
  var gridSrcset = function (u) { return GRID_WIDTHS.map(function (w) { return cdn(u, w) + ' ' + w + 'w'; }).join(', '); };

  // brand line -> brand-filtered PLP, in the island's own f=attr~value syntax
  // (decoded with decodeURIComponent, so spaces must be %20 not +)
  var vendorURL = function (v) {
    return ((window.routes && window.routes.all_products_url) || '/collections/all') + '?f=vendor~' + encodeURIComponent(v);
  };

  // Colour facet + swatch strips: metallic finishes get subtle gradients,
  // unknown values a neutral chip. Live values come from the swatch_value
  // metaobjects via window.ST_SWATCH (snippets/swatch-map.liquid); this table
  // is the unseeded-store fallback (moved here from typesense-plp.js).
  var SWATCH_FALLBACK = {
    'Black': '#1c1b19', 'White': '#f6f5f2', 'Grey': '#8d8d8d', 'Beige': '#d9c9ae',
    'Blue': '#33557f', 'Green': '#4a6b52', 'Pink': '#d8a8a8', 'Brown': '#6d4c33',
    'Red': '#a03328', 'Terracotta': '#b0553a',
    'Chrome': 'linear-gradient(135deg,#eceef0,#b9bcc0 45%,#e2e4e7)',
    'Stainless Steel': 'linear-gradient(135deg,#d6d8da,#aeb2b5 50%,#cfd2d4)',
    'Brushed Nickel': 'linear-gradient(135deg,#d3d1cd,#a8a6a2 50%,#c9c7c3)',
    'Gunmetal': 'linear-gradient(135deg,#63666b,#3e4146 55%,#5b5e63)',
    'Gold': 'linear-gradient(135deg,#e3c27e,#b9924a 55%,#d9b36a)',
    'Champagne': 'linear-gradient(135deg,#eeddba,#cbb387 55%,#e6d3ae)',
    'Copper': 'linear-gradient(135deg,#cd8a60,#a05f3c 55%,#c17a52)',
    'Bronze': 'linear-gradient(135deg,#9c7757,#6e4f38 55%,#8c6a4f)',
    'Woodgrain': 'linear-gradient(135deg,#9a6c45,#6f4a2d 55%,#8a5f3c)',
    'Mixed': 'linear-gradient(90deg,#1c1b19 0 25%,#b9924a 0 50%,#b9bcc0 0 75%,#6e4f38 0)',
    // Finish values past the Liquid cap: shop.metaobjects.<type>.values stops
    // at 50 entries (measured 2026-09-08: 50 of 64 come through, alphabetical
    // by handle), so window.ST_SWATCH silently lacks the tail. The seeded
    // finishes (scripts/swatch-setup.mjs) are repeated here as the net.
    'Matte Black': '#1e1e1e', 'Matt Black': '#1e1e1e', 'Black Chrome': '#2c2e30', 'Brushed Carbon': '#3a3a3c',
    'Polished Chrome': 'linear-gradient(135deg,#eceef0,#b9bcc0 45%,#e2e4e7)', 'Brushed Chrome': 'linear-gradient(135deg,#eceef0,#b9bcc0 45%,#e2e4e7)',
    'Nickel': 'linear-gradient(135deg,#d3d1cd,#a8a6a2 50%,#c9c7c3)', 'Satin Nickel': 'linear-gradient(135deg,#d3d1cd,#a8a6a2 50%,#c9c7c3)',
    'Brushed Stainless': 'linear-gradient(135deg,#d6d8da,#aeb2b5 50%,#cfd2d4)', 'Silver': 'linear-gradient(135deg,#dcdfe1,#b4b8bb 50%,#d0d3d6)',
    'Gun Metal': 'linear-gradient(135deg,#63666b,#3e4146 55%,#5b5e63)', 'Gunmetal Grey': 'linear-gradient(135deg,#63666b,#3e4146 55%,#5b5e63)', 'Brushed Gunmetal': 'linear-gradient(135deg,#63666b,#3e4146 55%,#5b5e63)',
    'Brushed Gold': 'linear-gradient(135deg,#d9bd85,#b08d57 55%,#cbab72)', 'Brushed Brass': 'linear-gradient(135deg,#d9bd85,#b08d57 55%,#cbab72)', 'Bright Gold': 'linear-gradient(135deg,#e6cf7a,#c9a227 55%,#dcbf5e)',
    'Rose Gold': 'linear-gradient(135deg,#e0b3a2,#c08871 55%,#d3a08d)', 'Brass': 'linear-gradient(135deg,#cbb87e,#a8934c 55%,#bfa96a)', 'Satin Brass': 'linear-gradient(135deg,#cbb87e,#a8934c 55%,#bfa96a)',
    'Polished Brass': 'linear-gradient(135deg,#cbb87e,#a8934c 55%,#bfa96a)', 'Antique Brass': 'linear-gradient(135deg,#cbb87e,#a8934c 55%,#bfa96a)', 'Aged Brass': 'linear-gradient(135deg,#cbb87e,#a8934c 55%,#bfa96a)',
    'Brushed Copper': 'linear-gradient(135deg,#cd8a60,#a05f3c 55%,#c17a52)', 'Brushed Bronze': 'linear-gradient(135deg,#9c7757,#6e4f38 55%,#8c6a4f)',
    'Matte White': '#f1f0ec', 'Gloss White': '#fafaf8',
    'Graphite': 'linear-gradient(135deg,#5b5d60,#36383b 55%,#54565a)', 'Natural Brass': 'linear-gradient(135deg,#d2b877,#a98a45 55%,#c7ad6b)',
    'Tumbled Brass': 'linear-gradient(135deg,#c4ac78,#9c8352 55%,#b9a26f)', 'Burnished': 'linear-gradient(135deg,#8f6b46,#5e4530 55%,#83603f)',
    'English Brass': 'linear-gradient(135deg,#c9a45c,#9a7a3a 55%,#bd9852)', 'Roma Bronze': 'linear-gradient(135deg,#7a5a44,#4f3829 55%,#6f5140)',
  };
  var swatchFill = function (attribute, value) {
    var published = (window.ST_SWATCH && window.ST_SWATCH.fills && window.ST_SWATCH.fills[attribute]) || {};
    return published[value] || SWATCH_FALLBACK[value] || '#cfccc6';
  };

  var STAR = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.45 6.19 20.5 7.3 14.03 2.6 9.45l6.5-.95z"/></svg>';
  var starRow = function () { return STAR + STAR + STAR + STAR + STAR; };

  // "Delivered between …" line. The index carries `available` only — no
  // stock tier — so every available tile quotes the supplier (slower, safer)
  // window; ui.js turns the offsets into dates. Same classes and data-*
  // contract as snippets/delivery-estimate.liquid.
  var deliveryLine = function (hit) {
    var d = window.ST_DELIVERY || {};
    if (!d.on) return '';
    if (hit.available === false) return '<p class="delivery-eta delivery-eta--compact delivery-eta--preorder">Available to pre-order</p>';
    return '<p class="delivery-eta delivery-eta--compact"><svg class="delivery-eta__tick" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="m8.2 12.2 2.6 2.6 5-5.6"></path></svg><span data-delivery-window data-min-days="' + esc(d.min) + '" data-max-days="' + esc(d.max) + '">Delivered in ' + esc(d.min) + '-' + esc(d.max) + ' business days</span></p>';
  };

  /* Badges. The ONLY badge authority is window.STBadges (assets/product-badges.js);
   * this renderer contributes markup, never a rule. */

  /* Does this product's price range have two ends?
   * `hit.price` is the CHEAPEST variant's price (the indexer's rule, so that
   * price and compare_at_price come from one variant — theme PR #225) and
   * `hit.price_max` the dearest. An index that predates price_max leaves it
   * undefined and this reads false, which is the behaviour this file had
   * before: one price, printed bare. The theme never needs the two repos to
   * ship in step. */
  var varies = function (hit) {
    return typeof hit.price_max === 'number' && typeof hit.price === 'number' && hit.price_max > hit.price;
  };
  /* Eyebrow, not a word in the price string: it shares the .card__rrp token
   * (theme.css) with the B2B "Retail" label that sits in exactly this slot. */
  var FROM = '<span class="card__from">From</span>';

  /* A saving worth striking through: the same $1.00 floor the Save badge uses
   * (window.STBadges.isSaving). Under it the compare-at is price-rule
   * rounding, not a discount — "$1,528.00 ~~$1,528.20~~" (Turner Hastings
   * staff, 2026-09-18). No resolver, no badge and no strike: the tile then
   * prints the plain price, which is never a false claim. */
  var saving = function (hit) {
    var B = window.STBadges;
    return !!(B && B.isSaving && B.isSaving(hit.price, hit.compare_at_price));
  };

  var badgeRow = function (hit, avail, b2b) {
    var B = window.STBadges;
    if (!B) return '';
    var list = B.resolve({
      availability: avail,
      tags: hit.tags,
      price: hit.price,
      compare_at_price: hit.compare_at_price,
      price_varies: varies(hit),
      available: hit.available,
      age_days: hit.created ? (Date.now() / 1000 - hit.created) / 86400 : -1,
      b2b: b2b,
      curated: B.fromHandles(hit.badges),
    }, Object.assign(B.opts('card'), { money: money }));
    if (!list.length) return '';
    return '<div class="card__flags">' + list.map(function (b) { return '<span class="' + esc(b.cls) + '">' + esc(b.label) + '</span>'; }).join('') + '</div>';
  };

  /* OPTIONAL add-to-cart facts, for a client rail whose products Liquid HAS
   * seen (snippets/card-json.liquid serialises them; the recently-viewed rail
   * is the only caller today).
   *
   * A Typesense document carries none of this — no variant ids, no metafields
   * — so `hit.atc` is undefined on the PLP and in the search dialog and those
   * two surfaces emit exactly the markup they emitted before this block
   * existed. Byte-identical: scripts/asset-sanity.mjs and the card's own
   * "outerHTML is the same before and after" contract still hold.
   *
   * WHAT IT BUYS. data-vid/data-nvar let a single-variant add skip the
   * /products/<handle>.js round trip, and the four shipping facts (data-pid,
   * data-vendor, data-ptype, data-bulky [+ data-dm]) are #413's contract: the
   * cart island bands the optimistic line on its FIRST painted frame instead
   * of guessing "not bulky" and correcting a round trip later. Luke, 2026-09-20:
   * "It should just show in bulky items without every showing in other items."
   * A rail add is an add; it gets the same guarantee.
   *
   * ABSENT IS NOT ZERO — see snippets/shipping-line-data.liquid. A doc with no
   * `atc` prints no data-bulky at all and the line renders band-less with no
   * shipping claim over it, rather than claiming to be light. */
  var atcData = function (hit) {
    var a = hit.atc;
    if (!a || a.vid == null) return '';
    var s = ' data-nvar="' + esc(a.nvar) + '"';
    if (Number(a.nvar) !== 1) return s;   // multi-variant goes to quick view; nothing to prefill
    s += ' data-vid="' + esc(a.vid) + '" data-price="' + esc(a.price) + '" data-title="' + esc(hit.title) + '"'
      + ' data-sku="' + esc(a.sku || '') + '" data-url="' + esc(a.url || ('/products/' + hit.handle)) + '"';
    if (a.pid != null) s += ' data-pid="' + esc(a.pid) + '"';
    s += ' data-vendor="' + esc(a.vendor || '') + '"' + (a.wh ? ' data-wh="' + esc(a.wh) + '"' : '') + ' data-ptype="' + esc(a.ptype || '') + '"';
    if (a.bulky != null) s += ' data-bulky="' + esc(a.bulky) + '"';
    if (a.dm) s += ' data-dm="' + esc(a.dm) + '"';
    if (a.img) s += ' data-img="' + esc(a.img) + '" data-imgw="' + esc(a.imgw || '') + '" data-imgh="' + esc(a.imgh || '') + '"';
    return s;
  };

  var ARROWS = '<button type="button" class="card__arrow card__arrow--prev" data-card-prev="true" aria-label="Previous image"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"></path></svg></button><button type="button" class="card__arrow card__arrow--next" data-card-next="true" aria-label="Next image"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"></path></svg></button>';

  /**
   * @param hit       a Typesense product document
   * @param position  1-based position in ITS grid (drives eager/priority image
   *                  hints: <=8 eager, <=4 high); undefined = lazy/auto
   */
  function render(hit, position) {
    // custom.availability as indexed (online | call_for_price | component |
    // in_store_only; missing = online). Components are filtered on the wire
    // when the theme setting is on; this is the belt for an older index.
    var avail = hit.availability || 'online';
    if (avail === 'component') return '';
    // Brand blackout (Luke, 2026-09-23; docs/decisions/2026-09-23-tile-brand-blackout.md):
    // every renderer of a hit reads the shopper's title and, for a brand-hidden
    // hit, no vendor. The Typesense app already blanks both for tiles; this is
    // the belt for card-json, recently-viewed and any older index document.
    if (window.STBrand) {
      var bh = window.STBrand.hidden(hit);
      hit = Object.assign({}, hit, { title: window.STBrand.title(hit, hit.title || ''), vendor: bh ? '' : hit.vendor });
    }
    var isCfp = avail === 'call_for_price';
    var isStore = avail === 'in_store_only';
    var isCfg = (hit.tags || []).indexOf('configurable') > -1;
    // B2B session: index prices are RETAIL, not the catalog price — label,
    // no Save badge/strike
    var b2b = !!(window.ST_CUSTOMER && window.ST_CUSTOMER.b2b);
    // the index carries up to 3 images per product — enough for the hover
    // carousel; ui.js reads the list off data-card-images
    var imgs = (hit.images || []).filter(Boolean).map(function (u) { return cdn(u, 800); });
    var pos = Number(position);
    var h = '';
    h += '<article class="card' + (isCfp ? ' card--cfp' : isStore ? ' card--store' : '') + '">';
    h += '<a class="card__link" href="/products/' + esc(hit.handle) + '" tabindex="-1" aria-label="' + esc(hit.title) + '">';
    h += '<div class="card__media media"' + (imgs.length > 1 ? ' data-card-images="' + esc(JSON.stringify(imgs)) + '"' : '') + ' style="aspect-ratio: var(--card-ratio, 1/1);">';
    if (hit.image) {
      h += '<img loading="' + (pos <= 8 ? 'eager' : 'lazy') + '" fetchpriority="' + (pos <= 4 ? 'high' : 'auto') + '" src="' + esc(cdn(hit.image, 460)) + '" srcset="' + esc(gridSrcset(hit.image)) + '" sizes="' + esc(GRID_SIZES) + '" width="480" height="600" alt="' + esc(hit.title) + '">';
    }
    if (imgs.length > 1) h += ARROWS;
    h += badgeRow(hit, avail, b2b);
    h += '<div class="card__actions"><button type="button" class="card__act card__act--save" data-wishlist="true" data-handle="' + esc(hit.handle) + '" data-title="' + esc(hit.title) + '" data-image="' + esc(hit.image || '') + '" data-price="' + esc(hit.price ? Math.round(Number(hit.price) * 100) : '') + '"'
      // the Liquid card carries data-variant; a Typesense hit has no variant id
      // to carry, so this appears only where one was serialised (card-json)
      + (hit.atc && hit.atc.vid != null ? ' data-variant="' + esc(hit.atc.vid) + '"' : '')
      + ' aria-label="Save to project"></button></div>';
    h += '<div class="card__cta"><button type="button" class="card__qview" data-quick-view="true" data-handle="' + esc(hit.handle) + '">Quickview</button>';
    if (isCfg) h += '<a class="card__act card__act--cfg" href="/products/' + esc(hit.handle) + '" data-cfg-prefetch="' + esc(hit.objectID || hit.id || '') + '" aria-label="Configure this product"></a>';
    else if (!(isCfp || isStore)) h += '<button type="button" class="card__act card__act--atc" data-card-atc="true" data-handle="' + esc(hit.handle) + '"' + atcData(hit) + ' aria-label="Add to cart"></button>';
    h += '</div></div></a>';
    if (hit.vendor) h += '<p class="card__vendor"><a href="' + esc(vendorURL(hit.vendor)) + '" aria-label="Shop all ' + esc(hit.vendor) + '">' + esc(hit.vendor) + '</a></p>';
    h += '<a class="card__link" href="/products/' + esc(hit.handle) + '"><div class="card__body">';
    if (hit.product_type) h += '<p class="card__type">' + esc(hit.product_type) + '</p>';
    h += '<h3 class="card__title">' + esc(hit.title) + '</h3>';
    h += deliveryLine(hit);
    if (hit.rating) h += '<span class="rating rating--card"><span class="rating__stars" style="--pct:' + esc((hit.rating / 5) * 100) + '%"><span class="rating__stars-row">' + starRow() + '</span><span class="rating__stars-fill" aria-hidden="true">' + starRow() + '</span></span><span class="rating__count">' + esc(hit.rating_count || '') + '</span></span>';
    /* PRICE. When the variants differ, the tile quotes the cheapest with a
     * "From" eyebrow and drops the struck compare-at: hit.compare_at_price is
     * the CHEAPEST variant's compare-at, so striking it beside a range quotes
     * one variant's RRP as the range's. The PDP still shows the selected
     * variant's RRP struck (pricing directive) — it prices one thing. */
    var v = varies(hit);
    if (isCfp) h += '<p class="card__price card__price--cfp">Call for price</p>';
    else if (b2b) h += '<p class="card__price card__price--rrp" title="Your trade price shows on the product page"><span class="card__rrp">Retail</span>' + (v ? FROM : '') + esc(money(hit.price)) + '</p>';
    else if (v) h += '<p class="card__price">' + FROM + esc(money(hit.price)) + '</p>';
    else h += '<p class="card__price">' + (saving(hit) ? '<span class="price-now">' + esc(money(hit.price)) + '</span> <s>' + esc(money(hit.compare_at_price)) + '</s>' : esc(money(hit.price))) + '</p>';
    h += '</div></a>';
    // per-finish previews: when the index carries finish_images ("Value::url")
    // the dots become hoverable previews that swap the tile photo to that
    // finish; otherwise the decorative colour dots
    var fmap = {};
    (hit.finish_images || []).forEach(function (s) { var i = s.indexOf('::'); if (i > 0) fmap[s.slice(0, i)] = s.slice(i + 2); });
    var fins = (hit.finishes || []).filter(function (f) { return fmap[f]; });
    if (fins.length > 1) {
      h += '<div class="card__swatches card__swatches--live">' + fins.slice(0, 6).map(function (f) {
        return '<span class="swatch swatch--live" data-fin-img="' + esc(cdn(fmap[f], 480)) + '" data-swatch-label="' + esc(f) + '" title="' + esc(f) + '" style="--sw: ' + esc(swatchFill('Finish', f) || swatchFill('Colour', f)) + ';"></span>';
      }).join('') + (fins.length > 6 ? '<span class="card__fin-more">+' + (fins.length - 6) + '</span>' : '') + '</div>';
    } else if ((hit.colours || []).length) {
      h += '<div class="card__swatches" aria-hidden="true">' + hit.colours.slice(0, 6).map(function (c) {
        return '<span class="swatch" data-swatch-label="' + esc(c) + '" style="--sw: ' + esc(swatchFill('Colour', c)) + ';"></span>';
      }).join('') + '</div>';
    }
    h += '</article>';
    return h;
  }

  // finish-preview hover: swap the tile image to the hovered finish's photo,
  // restore on leave. Delegated once at document level so it serves every
  // surface that renders a card (was PLP-only inside typesense-plp.js).
  document.addEventListener('mouseover', function (e) {
    var dot = e.target.closest && e.target.closest('[data-fin-img]');
    if (!dot) return;
    var img = dot.closest('.card') && dot.closest('.card').querySelector('.card__media img');
    if (!img) return;
    if (!img.dataset.origSrc) { img.dataset.origSrc = img.src; img.dataset.origSrcset = img.srcset || ''; }
    img.src = dot.dataset.finImg; img.removeAttribute('srcset');
  });
  document.addEventListener('mouseout', function (e) {
    var dot = e.target.closest && e.target.closest('[data-fin-img]');
    if (!dot) return;
    var img = dot.closest('.card') && dot.closest('.card').querySelector('.card__media img');
    if (img && img.dataset.origSrc) { img.src = img.dataset.origSrc; if (img.dataset.origSrcset) img.srcset = img.dataset.origSrcset; }
  });

  window.STCard = { render: render, cdn: cdn, gridSrcset: gridSrcset, GRID_SIZES: GRID_SIZES, GRID_WIDTHS: GRID_WIDTHS, swatchFill: swatchFill, money: money, esc: esc };
})();
